import Vue from 'vue'
import {
  Component,
  Watch
} from 'vue-property-decorator'
import { ROUTER_NAME } from '../../router'
import Course from '../../../api/v2/models/course'
import Review from '../../../api/v2/models/review'
import CourseLesson from '../../../api/v2/models/course_lesson'
import Material from '../../../api/v2/models/material'
import Banner from '../../../api/v2/models/banner'
import Chapter from '../../../api/v2/models/chapter'
import api from '../../../api/v2/api'
import UserQuestionList from '../organisms/UserQuestionList.vue'
import UserQuestionDialog from '../organisms/UserQuestionDialog.vue'
import trackingLesson from '../../trackings/lesson'
import { userState } from '../../stores/user'
import { checkMobile } from '../../utils/utils'
import { headerState } from '../../stores/header'
import ReviewDialog from '../organisms/ReviewDialog.vue'
import ReviewEditDialog from '../organisms/ReviewEditDialog.vue'
import markdownParser from '../../../../helpers/markdown-parser'
import CommentContainer from '../organisms/CommentContainer.vue'
import {Commentable} from '../../../api/v2/models/comment'
import {format} from 'date-fns'
import VoteForm from '../../../api/v2/forms/vote'
import { makeChannel, removeChannel } from '../../../../action_cable/cable'

interface LeftBarItem {
  id: number
  type: string // header, subheader, item
  icon: string
  image: string
  title: string
  action: Function
  chapterId?: number
}

@Component({
  components: {
    UserQuestionList,
    UserQuestionDialog,
    ReviewDialog,
    ReviewEditDialog,
    CommentContainer,
  }
})

export default class CourseDetail extends Vue {
  mobile = checkMobile()
  course: Course = null
  courseLessons: CourseLesson[] = []
  currentLesson: CourseLesson = null
  courseMaterials: Material[] = []
  lessonMaterials: Material[] = []
  completedLessonCount: number = 0
  banners: Banner[] = []
  review: Review = null
  chapters: Chapter[] = []
  isMobile: boolean = false
  leftDrawer = !this.mobile
  rightDrawer: boolean = false
  disabledNextBtn: boolean = false
  disabledPreviousBtn: boolean = false
  chatContent: string = ""
  markdownParser = markdownParser
  tab = null
  channel: any = null
  currentLessonQuestionsCount: number = 0
  mobileViewMode: 'lesson' | 'question' | 'material' = 'lesson'
  leftBarItems: LeftBarItem[] = []
  lazyLoading = true
  descriptionShowAll = false

  get user() {
    return userState.user
  }

  get lessonIds() {
    let dbLessonIds = this.courseLessons.map((cl) => cl.lesson.id)

    let lessonIds = []
    if (this.chapters.length > 0) {
      lessonIds = []
      this.chapters.forEach(chapter => {
        let chapterLessonIds = dbLessonIds.filter(id => chapter.lessonIds.includes(id))
        lessonIds = [...lessonIds, ...chapterLessonIds]
      })
    } else {
      lessonIds = dbLessonIds
    }

    return lessonIds
  }

  get schoolSetting() {
    return headerState.schoolSetting
  }

  get headerNavTextColor() {
    if (this.schoolSetting) {
      return this.schoolSetting.navTextColor
    }
  }

  get proccessCount() {
    if (this.courseLessons.length === 0) {
      return '--'
    }
    return ` ${Math.round(this.completedLessonCount / this.courseLessons.length * 100)}%(${this.completedLessonCount}/${this.courseLessons.length})`
  }

  get hamburgerColor() {
    if (this.schoolSetting) {
      return `color: ${this.schoolSetting.hamburgerColor}`
    }
    return "color: white"
  }

  get breadcrumbs() {
    return [
      {
        text: 'マイコース一覧',
        disabled: false,
        exact: true,
        to: '/classroom/my-courses/'
      },
      {
        text: this.currentLesson ? this.currentLesson.lesson.name : '',
        disabled: true
      }
    ]
  }

  get currentLessonId() {
    return this.$route.query['lesson_id'] as string || this.currentLesson.lesson.id
  }

  async created() {
    try {
      this.course = await api.course.my({
        id: this.$route.params.courseId
      })
    } catch (e) {
      location.href = `/courses/${this.$route.params.courseId}`
    }

    try {
      [this.chapters, this.courseLessons, this.courseMaterials, this.banners, this.review] =
        await Promise.all([
          api.chapter.index({
            courseId: this.$route.params.courseId,
            myCourse: true
          }),
          api.courseLesson.index({
            courseId: this.$route.params.courseId,
            mode: "simplify"
          }),
          api.course.materials({
            courseId: this.$route.params.courseId
          }),
          api.banner.index({
            type: "lesson"
          }),
          api.review.show(this.$route.params.courseId),
        ])

      this.setLeftBarMenu(this.chapters, this.courseLessons)
      this.completedLessonCount = this.courseLessons.filter(item => item.isLearnt).length

      if (this.courseLessons.length > 0) {
        let unlearntLessons = this.courseLessons.filter(lesson => !lesson.isLearnt);
        let lessonId = Number(this.$route.query['lesson_id']) || (unlearntLessons.length > 0 ? Number(unlearntLessons[0].lesson.id) : Number(this.courseLessons[0].lesson.id));
        this.currentLesson = await api.courseLesson.show({
          courseId: this.$route.params.courseId,
          id: lessonId
        })
        this.refetchQuestionsCount()
        this.lessonMaterials = await api.lesson.materials({
          lessonId: this.currentLesson.lesson.id.toString()
        })
      }

      document.addEventListener("visibilitychange", this.openOtherTab)

      // Scroll left sidebar to current lesson
      if (this.$route.query.lesson_id) {
        setTimeout(() => {
          const leftBarItem = document.getElementById(`left-bar-item-${this.$route.query.lesson_id}`)
          if (leftBarItem) {
            leftBarItem.scrollIntoView({
              behavior: "smooth",
              block: "center",
            })
          }
        }, 1000)
      }
    } catch (e) { }
  }

  openOtherTab() {
    if (document.hidden) {
      trackingLesson.stop()
      console.log("Tracking Stop")
    } else {
      if (this.currentLesson && this.user) {
        trackingLesson.start(this.user.currentSchoolId, this.currentLesson.lesson.id)
        console.log("Tracking Start")
      }
    }
  }

  mounted() {
    this.onResize()
    if (Array.isArray(this.$route.query.tab)) {
      this.tab = parseInt(this.$route.query.tab[0])
    } else {
      this.tab = parseInt(this.$route.query.tab)
    }
    window.addEventListener('resize', this.onResize)
  }

  async updateReview() {
    try {
      this.review = await api.review.show(this.$route.params.courseId)
    } catch (e) {
      console.error(e)
    }
  }

  goNextLesson() {
    if (this.lessonIds.indexOf(this.currentLesson.lesson.id) == this.lessonIds.length - 1) {
      this.disabledNextBtn = true
      return
    }
    let nextLessionIndex = this.lessonIds.indexOf(this.currentLesson.lesson.id) + 1
    this.goLesson(this.lessonIds[nextLessionIndex])
  }

  previousLession() {
    if (this.lessonIds.indexOf(this.currentLesson.lesson.id) == 0) {
      return
    }
    let previousLessionIndex = this.lessonIds.indexOf(this.currentLesson.lesson.id) - 1
    this.goLesson(this.lessonIds[previousLessionIndex])
  }

  getBanner(position: string): Banner | undefined {
    return this.banners.find(banner => banner.position == position)
  }

  setLeftBarMenu(chapters: Chapter[], courseLessons: CourseLesson[]) {
    let leftBarItems = [{
      id: this.course.id,
      type: 'header',
      icon: '',
      image: this.course.image.url,
      title: this.course.name,
      chapterId: null,
      action: () => {
        this.$router.push({
          name: ROUTER_NAME.MY_COURSES,
        })
      }
    }]
    if (chapters.length > 0) {
      chapters.forEach((item) => {
        const chapterLessons = courseLessons.filter((cl) => item.lessonIds.indexOf(cl.lesson.id) !== -1)
        if (chapterLessons.length > 0) {
          chapterLessons.forEach((courseLesson) => {
            leftBarItems.push({
              id: courseLesson.lesson.id,
              type: 'item',
              icon: courseLesson.isLearnt ? 'check' : ' ',
              image: '',
              title: `${!courseLesson.lesson.publishedAt ? '<span class="bg-danger p-1">非公開</span>' : ''}${courseLesson.lesson.name}`,
              chapterId: item.id,
              action: () => {
                this.goLesson(courseLesson.lesson.id)
              }
            })
          })
        }
      })
    } else {
      leftBarItems = leftBarItems.concat(
        courseLessons.map((item) => {
          return {
            id: item.lesson.id,
            type: 'item',
            icon: item.isLearnt ? 'check' : '',
            image: '',
            title: `${!item.lesson.publishedAt ? '<span class="bg-danger p-1">非公開</span>' : ''}${item.lesson.name}`,
            chapterId: null,
            action: () => {
              this.goLesson(item.lesson.id)
            }
          }
        })
      )
    }

    this.leftBarItems = leftBarItems
  }

  get leftBarSelectedItem() {
    if (this.$route.query.lesson_id) {
      return this.leftBarItems.findIndex((item) => item.id == Number(this.$route.query.lesson_id))
    }
    return -1
  }

  get headerTitle() {
    return this.currentLesson ? this.currentLesson.lesson.name : ''
  }

  goToHome() {
   const previousPage = sessionStorage.getItem('previousPage');
   if (previousPage) {
     sessionStorage.removeItem('previousPage');
     this.$router.push(previousPage)
   } else {
     this.$router.push({
       name: ROUTER_NAME.HOME
     })
   }
  }

  async goLesson(lessonId: number) {
    if (this.$route.query['lesson_id'] == lessonId.toString()) {
      return
    }

    this.descriptionShowAll = false
    this.$router.push({
      name: ROUTER_NAME.MY_COURSE_DETAIL,
      query: {
        lesson_id: lessonId.toString()
      }
    })
    this.mobileViewMode = 'lesson';
  }

  downloadFile(link: string) {
    window.open(`${location.origin}/${link}`)
  }

  showRightMenu() {
    this.rightDrawer = !this.rightDrawer
  }

  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize)
    }
  }

  onResize() {
    this.isMobile = window.innerWidth < 600
  }

  get commentable(): Commentable {
    return {
      commentableId: this.currentLesson.lesson.id,
      commentableType: "Lesson"
    }
  }

  private isProcessingFinish = false
  private isProcessingRevert = false  // revert用のフラグを追加

  async finishLesson() {
    if (this.isProcessingFinish) {
      return
    }
  
    console.log('=== finishLesson called ===', new Date().toISOString());  // 追加
  
    try {
      this.isProcessingFinish = true
      await api.lesson.learnt({
        courseId: this.$route.params.courseId,
        lessonId: this.currentLesson.lesson.id.toString()
      })
      console.log('=== api.lesson.learnt completed ===');  // 追加
      this.currentLesson.isLearnt = true
      this.completedLessonCount++
      const lessonIndex = this.leftBarItems.findIndex(
        (item) => item.id.toString() == this.currentLessonId && item.type == 'item'
      )
      if (this.leftBarItems[lessonIndex]) {
        this.leftBarItems[lessonIndex].icon = 'check'
        this.leftBarItems = this.leftBarItems
      }
    } finally {
      this.isProcessingFinish = false
    }
  }

  async revertFinishLesson() {
    if (this.isProcessingRevert) {
      return
    }

    try {
      this.isProcessingRevert = true
      await api.lesson.revertLearnt({
        courseId: this.$route.params.courseId,
        lessonId: this.currentLesson.lesson.id.toString()
      })
      this.currentLesson.isLearnt = false
      this.completedLessonCount--
      const lessonIndex = this.leftBarItems.findIndex(
        (item) => item.id.toString() == this.currentLessonId && item.type == 'item'
      )
      if (this.leftBarItems[lessonIndex]) {
        this.leftBarItems[lessonIndex].icon = ''
        this.leftBarItems = this.leftBarItems
      }
    } finally {
      this.isProcessingRevert = false
    }
  }

  @Watch('$route')
  async changeRoute(val, oldVal) {
    if (val.query.lesson_id && val.query.lesson_id == oldVal.query.lesson_id) {
      return
    }
    if (!this.currentLesson) return
    try {
      let unlearntLessons = this.courseLessons.filter(lesson => !lesson.isLearnt).slice(0, 1);
      let lessonId = Number(this.$route.query['lesson_id']) || (unlearntLessons.length > 0 ? Number(unlearntLessons[0].lesson.id) : Number(this.courseLessons[0].lesson.id))
      this.currentLesson = await api.courseLesson.show({
        courseId: this.$route.params.courseId,
        id: lessonId
      });

      this.lessonMaterials = await api.lesson.materials({
        lessonId: this.currentLesson?.lesson?.id?.toString()
      })
    } catch (error) {
      console.error(error)
    }

    this.$nextTick(function () {
      if (this.currentLesson.lesson.lessonType != 'live' && !this.tab) {
        this.tab = 1
      }

      if (this.currentLesson.lesson.lessonType == 'live') {
        this.tab = 0
      }
    })
    this.refetchQuestionsCount()
  }

  @Watch('currentLesson', {immediate: true})
  changeCurrentLesson() {
    if (!this.currentLesson) {
      return
    }
    this.disabledNextBtn = false
    this.disabledPreviousBtn = false
    if (this.lessonIds.indexOf(this.currentLesson.lesson.id) == this.lessonIds.length - 1) {
      this.disabledNextBtn = true
    }

    if (this.lessonIds.indexOf(this.currentLesson.lesson.id) == 0) {
      this.disabledPreviousBtn = true
    }

    if (this.currentLesson) {
      headerState.setTitle(this.currentLesson.lesson.name)
      if (this.user?.currentSchoolId) {
        trackingLesson.start(this.user.currentSchoolId, this.currentLesson.lesson.id)
      }

      setTimeout(() => {
        if (this.isMobile) {
          window.scrollTo({
            top: 0,
            behavior: 'instant',
          })
        } else {
          document.getElementById("classroom-lesson-body")?.scrollIntoView({
            behavior: "instant"
          })
        }
      }, 100)

      // 目次にスクロール
      setTimeout(() => {
        var elements = document.getElementsByClassName("mokuji")
        for (var i = 0; i < elements.length; i++) {
          const element = elements[i]
          element.addEventListener('click', (e) => {
            e.preventDefault()
            var id = (element as any).hash
            if (id) {
              id = (id as string).replace('#', '')
              const desElm = document.getElementById(id)
              if (desElm) {
                desElm.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                  inline: "center"
                })
              }
            }
          }, false)
        }
      }, 100)
      this.refetchQuestionsCount()
    }

    // lazyLoading設定
    // 動画レンダリングの問題があり、少しtimelagを設定します。
    this.lazyLoading = false
    setTimeout(() => {
      this.lazyLoading = true
    }, 1)
  }

  async upVote() {
    if (this.currentLesson.lesson.voteMark >= 1) {
      return
    }
    try {
      const voteForm = new VoteForm()
      voteForm.value = this.currentLesson.lesson.voteMark + 1
      voteForm.votableId = this.currentLesson.lesson.id
      voteForm.votableType = "Lesson"
      await api.vote.create(voteForm)
      this.currentLesson.lesson.voteMark = voteForm.value
      this.currentLesson.lesson.voteValues += 1
    } catch (e) {
    }
  }

  async downVote() {
    if (this.currentLesson.lesson.voteMark <= 0) {
      return
    }
    try {
      const voteForm = new VoteForm()
      voteForm.value = this.currentLesson.lesson.voteMark - 1
      voteForm.votableId = this.currentLesson.lesson.id
      voteForm.votableType = "Lesson"
      await api.vote.create(voteForm)
      this.currentLesson.lesson.voteMark = voteForm.value
      this.currentLesson.lesson.voteValues -= 1
    } catch (e) {
    }
  }

  destroyed() {
    trackingLesson.stop()
  }

  dateFormat(date: Date) {
    return format(date, 'YYYY/MM/DD HH:mm')
  }

  createChannel() {
    removeChannel('LessonQuestionCountChannel')

    let unlearntLessons = this.courseLessons.filter(lesson => !lesson.isLearnt).slice(0, 1);
    let lessonId = Number(this.$route.query['lesson_id']) || (unlearntLessons.length > 0 ? Number(unlearntLessons[0].lesson.id) : Number(this.courseLessons[0].lesson.id))
    this.channel = makeChannel(
      'LessonQuestionCountChannel',
      async (e) => {
        let courseLesson: CourseLesson = await api.courseLesson.show({
          courseId: this.$route.params.courseId,
          id: lessonId
        })
        this.currentLessonQuestionsCount = courseLesson.lesson.questionsCount
      },
      () => {
        this.channel.send({
          action: 'follow',
          data: {
            relativeable_id: lessonId,
            relativeable_type: "Lesson"
          }
        })
      },
    )
  }

  refetchQuestionsCount() {
    this.createChannel()
    this.currentLessonQuestionsCount = this.currentLesson.lesson.questionsCount
  }
}
